import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { proveedoresAPI, tiposProveedoresAPI, provinciasAPI, localidadesAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import '../Clientes.css';

function validarCuit(val) {
  const soloNumeros = String(val || '').replace(/\D/g, '');
  if (soloNumeros.length !== 11) return false;
  const digitos = soloNumeros.split('').map(Number);
  const multiplicadores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let acumulado = 0;
  for (let i = 0; i < 10; i++) acumulado += digitos[i] * multiplicadores[i];
  let verif = 11 - (acumulado % 11);
  if (verif === 11) verif = 0;
  else if (verif === 10) verif = 9;
  return digitos[10] === verif;
}

const ProveedorForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [showNuevaLocalidad, setShowNuevaLocalidad] = useState(false);
  const [nuevaLocalidad, setNuevaLocalidad] = useState({ nombre: '', codigo_postal: '' });
  const [loadingLocalidad, setLoadingLocalidad] = useState(false);
  const [formData, setFormData] = useState({
    cuit: '',
    razon_social: '',
    direccion: '',
    telefono: '',
    celular: '',
    email: '',
    provincia_id: '',
    localidad_id: '',
    tipo_id: '',
    activo: true
  });
  const [cuitError, setCuitError] = useState('');

  const loadTipos = useCallback(async () => {
    try {
      const response = await tiposProveedoresAPI.getAll();
      if (response.success) setTipos(response.data);
    } catch (error) {
      toast.error('Error al cargar tipos');
    }
  }, []);

  const loadProvincias = useCallback(async () => {
    try {
      const response = await provinciasAPI.getAll();
      if (response.success) setProvincias(response.data);
    } catch (error) {
      toast.error('Error al cargar provincias');
    }
  }, []);

  const loadLocalidades = useCallback(async (provinciaId) => {
    if (!provinciaId) {
      setLocalidades([]);
      return;
    }
    try {
      const response = await localidadesAPI.getByProvincia(provinciaId);
      if (response.success) setLocalidades(response.data);
      else setLocalidades([]);
    } catch (error) {
      setLocalidades([]);
    }
  }, []);

  const loadProveedor = useCallback(async () => {
    try {
      const response = await proveedoresAPI.getById(id);
      if (response.success) {
        const data = {
          ...response.data,
          tipo_id: response.data.tipo_id || '',
          provincia_id: response.data.provincia_id || '',
          localidad_id: response.data.localidad_id || ''
        };
        setFormData(data);
        if (data.cuit && !validarCuit(data.cuit)) setCuitError('CUIT inválido.');
        else setCuitError('');
        if (data.provincia_id) {
          const locResponse = await localidadesAPI.getByProvincia(data.provincia_id);
          if (locResponse.success) setLocalidades(locResponse.data);
        }
      }
    } catch (error) {
      toast.error('Error al cargar proveedor');
      navigate('/proveedores');
    }
  }, [id, navigate]);

  useEffect(() => {
    loadTipos();
    loadProvincias();
    if (id) loadProveedor();
  }, [id, loadTipos, loadProvincias, loadProveedor]);

  useEffect(() => {
    if (formData.provincia_id) {
      loadLocalidades(formData.provincia_id);
    } else {
      setLocalidades([]);
      setFormData(prev => ({ ...prev, localidad_id: '' }));
    }
  }, [formData.provincia_id, loadLocalidades]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'cuit') {
      const soloNumeros = String(value).replace(/\D/g, '');
      if (soloNumeros.length === 0) setCuitError('');
      else if (soloNumeros.length === 11) setCuitError(validarCuit(value) ? '' : 'CUIT inválido.');
      else setCuitError('');
    }
  };

  const handleCuitBlur = () => {
    const cuit = formData.cuit?.trim();
    if (!cuit) { setCuitError(''); return; }
    setCuitError(validarCuit(cuit) ? '' : 'CUIT inválido. Verifique los 11 dígitos.');
  };

  const handleCrearLocalidad = async (e) => {
    e.preventDefault();
    if (!formData.provincia_id) {
      toast.error('Debe seleccionar una provincia primero');
      return;
    }
    if (!nuevaLocalidad.nombre.trim()) {
      toast.error('El nombre de la localidad es requerido');
      return;
    }
    setLoadingLocalidad(true);
    try {
      const response = await localidadesAPI.create({
        nombre: nuevaLocalidad.nombre.trim(),
        provincia_id: formData.provincia_id,
        codigo_postal: nuevaLocalidad.codigo_postal || null
      });
      if (response.success) {
        toast.success(response.message || 'Localidad creada');
        await loadLocalidades(formData.provincia_id);
        setFormData(prev => ({ ...prev, localidad_id: response.data.id }));
        setShowNuevaLocalidad(false);
        setNuevaLocalidad({ nombre: '', codigo_postal: '' });
      }
    } catch (error) {
      toast.error(error.message || 'Error al crear localidad');
    } finally {
      setLoadingLocalidad(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cuit = formData.cuit?.trim();
    if (cuit && !validarCuit(cuit)) {
      toast.error('El CUIT ingresado no es válido.');
      setCuitError('CUIT inválido.');
      return;
    }
    setLoading(true);
    try {
      let response;
      if (id) {
        response = await proveedoresAPI.update(id, formData);
      } else {
        response = await proveedoresAPI.create(formData);
      }
      if (response.success) {
        toast.success(id ? 'Proveedor actualizado' : 'Proveedor creado');
        navigate('/proveedores');
      }
    } catch (error) {
      toast.error(error.message || 'Error al guardar proveedor');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('¿Está seguro de eliminar este proveedor? Esta acción no se puede deshacer.')) return;
    setLoading(true);
    try {
      const response = await proveedoresAPI.delete(id);
      if (response.success) {
        toast.success('Proveedor eliminado');
        navigate('/proveedores');
      }
    } catch (error) {
      toast.error(error.message || 'Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cliente-form">
      <h1>{id ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h1>

      <form onSubmit={handleSubmit} className="form-container">
        <div className="form-row">
          <div className="form-group">
            <label>CUIT *</label>
            <input
              type="text"
              name="cuit"
              value={formData.cuit}
              onChange={handleChange}
              onBlur={handleCuitBlur}
              required
              placeholder="20-12345678-9 o 11 dígitos"
              className={cuitError ? 'input-error' : ''}
            />
            {cuitError && <span className="form-error">{cuitError}</span>}
          </div>
          <div className="form-group">
            <label>Razón Social *</label>
            <input
              type="text"
              name="razon_social"
              value={formData.razon_social}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Tipo *</label>
            <select name="tipo_id" value={formData.tipo_id} onChange={handleChange} required>
              <option value="">Seleccione un tipo</option>
              {tipos.map(t => (
                <option key={t.id} value={t.id}>{t.nombre}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Dirección</label>
            <input type="text" name="direccion" value={formData.direccion} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Provincia</label>
            <select name="provincia_id" value={formData.provincia_id} onChange={handleChange}>
              <option value="">Seleccione una provincia</option>
              {provincias.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Localidad</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <select
                name="localidad_id"
                value={formData.localidad_id}
                onChange={handleChange}
                disabled={!formData.provincia_id}
                style={{ flex: 1 }}
              >
                <option value="">Seleccione una localidad</option>
                {localidades.map(l => (
                  <option key={l.id} value={l.id}>{l.nombre}</option>
                ))}
              </select>
              {formData.provincia_id && (
                <button type="button" onClick={() => setShowNuevaLocalidad(true)} className="btn-secondary" style={{ padding: '8px 12px', fontSize: '14px', whiteSpace: 'nowrap' }} title="Agregar nueva localidad">
                  + Nueva
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Teléfono</label>
            <input type="text" name="telefono" value={formData.telefono} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Celular</label>
            <input type="text" name="celular" value={formData.celular} onChange={handleChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label className="form-toggle-wrap">
              <span className="toggle-switch-box">
                <input type="checkbox" name="activo" checked={formData.activo} onChange={handleChange} />
                <span className="form-toggle-switch" />
              </span>
              <span className="form-toggle-label">{formData.activo ? 'Desactivar' : 'Activar'}</span>
            </label>
          </div>
        </div>

        <div className="form-actions" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" onClick={() => navigate('/proveedores')} className="btn-secondary">Cancelar</button>
            {id && hasPermission('eliminar_clientes') && (
              <button type="button" onClick={handleDelete} disabled={loading} className="btn-delete">Eliminar proveedor</button>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>

      {showNuevaLocalidad && (
        <div
          className="modal-overlay"
          onClick={() => { setShowNuevaLocalidad(false); setNuevaLocalidad({ nombre: '', codigo_postal: '' }); }}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ backgroundColor: 'white', padding: 24, borderRadius: 8, maxWidth: 500, width: '90%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <h2 style={{ marginTop: 0, marginBottom: 20 }}>Nueva Localidad</h2>
            <form onSubmit={handleCrearLocalidad}>
              <div className="form-group">
                <label>Provincia</label>
                <input type="text" value={provincias.find(p => p.id === parseInt(formData.provincia_id))?.nombre || ''} disabled style={{ backgroundColor: '#f5f5f5' }} />
              </div>
              <div className="form-group">
                <label>Nombre de la Localidad *</label>
                <input type="text" value={nuevaLocalidad.nombre} onChange={e => setNuevaLocalidad(prev => ({ ...prev, nombre: e.target.value }))} placeholder="Ej: Villa Nueva" required autoFocus />
              </div>
              <div className="form-group">
                <label>Código Postal (opcional)</label>
                <input type="text" value={nuevaLocalidad.codigo_postal} onChange={e => setNuevaLocalidad(prev => ({ ...prev, codigo_postal: e.target.value }))} placeholder="Ej: 5000" maxLength={10} />
              </div>
              <div className="form-actions" style={{ marginTop: 20 }}>
                <button type="button" onClick={() => { setShowNuevaLocalidad(false); setNuevaLocalidad({ nombre: '', codigo_postal: '' }); }} className="btn-secondary" disabled={loadingLocalidad}>Cancelar</button>
                <button type="submit" disabled={loadingLocalidad} className="btn-primary">{loadingLocalidad ? 'Creando...' : 'Crear Localidad'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProveedorForm;
